import React, { useState } from 'react';
import { getTenantId } from '../utils/tenant';
import { getAccessToken } from '../utils/storage';

interface EmbeddingStats {
  tenant_id: string;
  node_counts: {
    Table: number;
    Column: number;
  };
  relationship_counts: {
    HAS_COLUMN: number;
    RELATED_TO: number;
  };
  total_nodes: number;
  total_relationships: number;
  has_vector_index: boolean;
  status: string;
}

interface GraphHealth {
  status: string;
  timestamp: string;
  services: {
    timestamp: number;
    chroma: {
      status: string;
      available: boolean;
    };
    neo4j: {
      status: string;
      available: boolean;
    };
    hybrid_mode: boolean;
    overall_status: string;
  };
  neo4j_available: boolean;
}

interface SystemSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
}

const SystemSettings: React.FC<SystemSettingsProps> = ({ isOpen, onClose, onBack }) => {
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showHealthModal, setShowHealthModal] = useState(false);
  const [stats, setStats] = useState<EmbeddingStats | null>(null);
  const [health, setHealth] = useState<GraphHealth | null>(null);
  const [loading, setLoading] = useState(false);
  const [healthLoading, setHealthLoading] = useState(false);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmenuClick = (submenu: string) => {
    setActiveSubmenu(activeSubmenu === submenu ? null : submenu);
  };

  const handleStatsClick = async () => {
    setLoading(true);
    setError(null);
    try {
      const tenantId = getTenantId();
      const accessToken = getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/embeddings/stats/${tenantId}`, {
        headers: {
          ...(accessToken && { Authorization: `Bearer ${accessToken}` })
        }
      });
      if (!response.ok) throw new Error('Failed to fetch stats');
      const data = await response.json();
      setStats(data);
      setShowStatsModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch stats');
    } finally {
      setLoading(false);
    }
  };

  const handleInitializeClick = async () => {
    setInitializing(true);
    setError(null);
    setSuccess(null);
    try {
      const tenantId = getTenantId();
      const accessToken = getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/embeddings/initialize/${tenantId}`, {
        method: 'POST',
        headers: {
          ...(accessToken && { Authorization: `Bearer ${accessToken}` })
        }
      });
      if (!response.ok) throw new Error('Failed to initialize embeddings');
      setSuccess('Embeddings initialized successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize embeddings');
    } finally {
      setInitializing(false);
    }
  };

  const handleHealthClick = async () => {
    setHealthLoading(true);
    setError(null);
    try {
      const accessToken = getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/graph/health`, {
        headers: {
          ...(accessToken && { Authorization: `Bearer ${accessToken}` })
        }
      });
      if (!response.ok) throw new Error('Failed to fetch health');
      const data = await response.json();
      setHealth(data);
      setShowHealthModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch health');
    } finally {
      setHealthLoading(false);
    }
  };

  const handleCreateSchemaClick = async () => {
    setSchemaLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const tenantId = getTenantId();
      const accessToken = getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/graph/schema/${tenantId}`, {
        method: 'POST',
        headers: {
          ...(accessToken && { Authorization: `Bearer ${accessToken}` })
        }
      });
      if (!response.ok) throw new Error('Failed to create schema graph');
      setSuccess('Schema graph created successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create schema graph');
    } finally {
      setSchemaLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-lg p-6 w-96 max-w-90vw shadow-xl border border-slate-200 dark:border-slate-700">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <button
              onClick={onBack}
              className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                arrow_back
              </span>
            </button>
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">System Settings</h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
              close
            </span>
          </button>
        </div>
        
        <div className="space-y-3">
          {/* Neo4j Embeddings */}
          <div className="border border-slate-200 dark:border-slate-700 rounded-lg">
            <button
              onClick={() => handleSubmenuClick('embeddings')}
              className="w-full flex items-center justify-between p-4 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors rounded-lg"
            >
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0 p-2 rounded-lg bg-purple-100 dark:bg-purple-900">
                  <span className="material-symbols-outlined text-purple-600 dark:text-purple-400" style={{ fontSize: '20px' }}>
                    psychology
                  </span>
                </div>
                <div className="text-left">
                  <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Neo4j Embeddings</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Manage vector embeddings</p>
                </div>
              </div>
              <span className={`material-symbols-outlined text-slate-400 dark:text-slate-500 transition-transform ${activeSubmenu === 'embeddings' ? 'rotate-90' : ''}`} style={{ fontSize: '16px' }}>
                chevron_right
              </span>
            </button>
            
            {activeSubmenu === 'embeddings' && (
              <div className="border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
                <button 
                  onClick={handleStatsClick}
                  disabled={loading}
                  className="w-full text-left px-6 py-3 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Loading...' : 'Stats'}
                </button>
                <button 
                  onClick={handleInitializeClick}
                  disabled={initializing}
                  className="w-full text-left px-6 py-3 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                >
                  {initializing ? 'Initializing...' : 'Initialize'}
                </button>
              </div>
            )}
          </div>

          {/* Neo4j Graph */}
          <div className="border border-slate-200 dark:border-slate-700 rounded-lg">
            <button
              onClick={() => handleSubmenuClick('graph')}
              className="w-full flex items-center justify-between p-4 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors rounded-lg"
            >
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0 p-2 rounded-lg bg-orange-100 dark:bg-orange-900">
                  <span className="material-symbols-outlined text-orange-600 dark:text-orange-400" style={{ fontSize: '20px' }}>
                    account_tree
                  </span>
                </div>
                <div className="text-left">
                  <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Neo4j Graph</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Manage graph database</p>
                </div>
              </div>
              <span className={`material-symbols-outlined text-slate-400 dark:text-slate-500 transition-transform ${activeSubmenu === 'graph' ? 'rotate-90' : ''}`} style={{ fontSize: '16px' }}>
                chevron_right
              </span>
            </button>
            
            {activeSubmenu === 'graph' && (
              <div className="border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
                <button 
                  onClick={handleHealthClick}
                  disabled={healthLoading}
                  className="w-full text-left px-6 py-3 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                >
                  {healthLoading ? 'Loading...' : 'Health'}
                </button>
                <button 
                  onClick={handleCreateSchemaClick}
                  disabled={schemaLoading}
                  className="w-full text-left px-6 py-3 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                >
                  {schemaLoading ? 'Creating...' : 'Create Schema Graph'}
                </button>
              </div>
            )}
          </div>
        </div>
        
        {error && (
          <div className="mt-4 p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded-lg text-sm">
            {error}
          </div>
        )}
        
        {success && (
          <div className="mt-4 p-3 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-lg text-sm">
            {success}
          </div>
        )}
      </div>
      
      {/* Stats Modal */}
      {showStatsModal && stats && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 w-96 max-w-90vw shadow-xl border border-slate-200 dark:border-slate-700">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">Embedding Stats</h3>
              <button
                onClick={() => setShowStatsModal(false)}
                className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>close</span>
              </button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">Status:</span>
                <span className={`font-medium ${stats.status === 'active' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {stats.status}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">Total Nodes:</span>
                <span className="font-medium text-slate-800 dark:text-slate-200">{stats.total_nodes}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">Total Relationships:</span>
                <span className="font-medium text-slate-800 dark:text-slate-200">{stats.total_relationships}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">Tables:</span>
                <span className="font-medium text-slate-800 dark:text-slate-200">{stats.node_counts.Table}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">Columns:</span>
                <span className="font-medium text-slate-800 dark:text-slate-200">{stats.node_counts.Column}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">Vector Index:</span>
                <span className={`font-medium ${stats.has_vector_index ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {stats.has_vector_index ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Health Modal */}
      {showHealthModal && health && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 w-96 max-w-90vw shadow-xl border border-slate-200 dark:border-slate-700">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">Graph Health</h3>
              <button
                onClick={() => setShowHealthModal(false)}
                className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>close</span>
              </button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">Overall Status:</span>
                <span className={`font-medium ${health.status === 'healthy' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {health.status}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">Neo4j:</span>
                <span className={`font-medium ${health.services.neo4j.status === 'healthy' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {health.services.neo4j.status}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">Chroma:</span>
                <span className={`font-medium ${health.services.chroma.status === 'healthy' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {health.services.chroma.status}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">Hybrid Mode:</span>
                <span className={`font-medium ${health.services.hybrid_mode ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {health.services.hybrid_mode ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemSettings;